import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { companyInfo } from '../../data/mock';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';

const OnePageHeader = () => {
  const { t, language, changeLanguage, isRTL } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const [oneActive, setOneActive] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // listen for onepage active section events so this header can highlight
  useEffect(() => {
    const handler = (e) => {
      if (e && e.detail) setOneActive(e.detail);
    };
    window.addEventListener('onepage-active', handler);
    return () => window.removeEventListener('onepage-active', handler);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home'), section: 'home' },
    { path: '/technology', label: t('nav.technology'), section: 'technology' },
    { path: '/services', label: t('nav.services'), section: 'services' },
    { path: '/track-record', label: t('nav.trackRecord'), section: 'footprint' },
    { path: '/contact', label: t('nav.contact'), section: 'contact' },
  ];

  const languages = [
    { code: 'en', label: 'EN', fullLabel: 'English' },
    { code: 'ru', label: 'RU', fullLabel: 'Русский' },
    { code: 'ar', label: 'AR', fullLabel: 'العربية' },
  ];

  const isActive = (path, section) => {
    // if oneActive present (we're in one_page), use that to highlight
    if (oneActive) return oneActive === section;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleScrollTo = (section) => {
    // immediately mark as active for instant feedback, then dispatch scroll
    setOneActive(section);
    window.dispatchEvent(new CustomEvent('onepage-scroll', { detail: section }));
    setMobileMenuOpen(false);
  };

  return (
    <header
      style={{ backgroundColor: 'rgba(149, 149, 149, 0.22)' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg shadow-black/20' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={companyInfo.logo}
              alt="Petroservice"
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
              style={{ height: '4rem' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => handleScrollTo(link.section)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path, link.section)
                    ? 'text-red-500 bg-red-500/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Language Selector & Mobile Menu */}
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 text-gray-300 hover:text-white hover:bg-white/5 px-3"
                >
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {languages.find((l) => l.code === language)?.label}
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="bg-zinc-900 border-zinc-800">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${
                      language === lang.code ? 'text-red-500 bg-red-500/10' : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span className="font-medium">{lang.label}</span>
                    <span className="ml-2 text-gray-500">{lang.fullLabel}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-300 hover:text-white transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-zinc-800">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => handleScrollTo(link.section)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.path, link.section)
                      ? 'text-red-500 bg-red-500/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default OnePageHeader;
